import { chooseNextCharger, displayResults, addChargerAmounts } from './routeHelper.js'
import pointToLineDistance from '@turf/point-to-line-distance'
import nearestPointOnLine from '@turf/nearest-point-on-line'

const distanceFromRoute = 4

export default {
  state: {
    chargers: false, // combined evroam and chargenet chargers along the route
    chargersIsLoaded: false,
    route: false,
    routeIsLoaded: false,
    originCoords: [],
    destinationCoords: [],
    waypointCoords: {},
    originInfo: null,
    destinationInfo: null,
    waypointInfo: {},
    filteredEvroamData: {},
    filteredEvroamIsLoaded: false,
    filteredChargenetData: {},
    filteredChargenetIsLoaded: false,
    predictions: [],
    predictionsIsLoaded: false,
    processing: false
  },
  mutations: {
    updateChargers (state, payload) {
      console.log('Mutation: route/updateChargers')
      state.chargers = payload
      state.chargersIsLoaded = true
    },
    updateRoute (state, payload) {
      console.log('Mutation: route/updateRoute')
      state.route = payload
      state.routeIsLoaded = true
    },
    updateFilteredEvroamData (state, payload) {
      console.log('Mutation: route/updateFilteredEvroamData')
      state.filteredEvroamData = payload
      state.filteredEvroamIsLoaded = true
    },
    updateFilteredChargenetData (state, payload) {
      console.log('Mutation: route/updateChargenetEvroamData')
      state.filteredChargenetData = payload
      state.filteredChargenetIsLoaded = true
    },
    updateOrigin (state, payload) {
      console.log('Mutation: route/updateOrigin')
      state.originCoords = payload.geometry.coordinates
      state.originInfo = payload
    },
    updateDestination (state, payload) {
      console.log('Mutation: route/updateDestination')
      state.destinationCoords = payload.geometry.coordinates
      state.destinationInfo = payload
    },
    updateWaypoint (state, payload) {
      console.log('Mutation: route/updateWaypoint')
      state.waypointCoords[payload.name] = payload.data.geometry.coordinates
      state.waypointInfo[payload.name] = payload.data
    },
    updatePredictions (state, payload) {
      console.log('Mutation: route/updatePredictions')
      state.predictions = payload
      state.predictionsIsLoaded = true
    },
    clearWaypoint (state, payload) {
      console.log('Mutation: route/clearWaypoint')
      state.waypointCoords[payload.name] = null
      state.waypointInfo[payload.name] = null
    },
    clearOrigin (state, payload) {
      console.log('Mutation: route/clearOrigin')
      state.originCoords = null
      state.originInfo = null
    },
    clearDestination (state, payload) {
      console.log('Mutation: route/clearDestination')
      state.destinationCoords = null
      state.destinationInfo = null
    },
    toggleProcessing (state) {
      console.log('Mutation: route/toggleProcessing')
      state.processing ? state.processing = false : state.processing = true
    }
  },
  actions: {
    processRoute: function (context) {
      // Must update chargers and route BEFORE running this action
      console.log('Action: route/processRoute')

      if (context.state.chargers && context.state.route) {
        const chargers = Object.assign(context.state.chargers)
        chooseNextCharger(chargers, 0, context.state.predictions)
        addChargerAmounts(chargers, 0, context.state.predictions)
        context.commit('updateChargers', chargers)
        displayResults(context.state.chargers, context.state.predictions)
      } else {
        console.log('Chargers and route are not present')
      }
      context.commit('toggleProcessing')
    },
    addChargersOnRoute: function (context) {
      console.log('Action: route/addChargersOnRoute')
      const chargenetIsLoaded = context.rootState.chargenet.isLoaded
      const evroamIsLoaded = context.rootState.evroam.isLoaded
      const routeIsLoaded = context.state.routeIsLoaded
      if (chargenetIsLoaded && evroamIsLoaded && routeIsLoaded) {
        const lineGeometry = context.state.route.geometry
        const evroamData = context.rootState.evroam.data
        const chargenetData = context.rootState.chargenet.data
        const chargersOnRoute = {}

        // Find distance from route to each evroam charger
        for (const i in evroamData.features) {
          const point = evroamData.features[i].geometry
          const distance = pointToLineDistance(point, lineGeometry) // in km
          // Extract only close chargers
          if (distance <= distanceFromRoute) {
            chargersOnRoute[evroamData.features[i].properties.siteId] = ({
              distanceToLine: distance,
              feature: evroamData.features[i],
              source: 'evroam'
            })
          }
        }

        // Find distance from route to each chargenet charger
        for (const i in chargenetData.features) {
          const point = chargenetData.features[i].geometry
          const distance = pointToLineDistance(point, lineGeometry) // in km
          // Extract only close chargers
          if (distance <= distanceFromRoute) {
            chargersOnRoute[chargenetData.features[i].properties.name] = ({
              distanceToLine: distance,
              feature: chargenetData.features[i],
              source: 'chargenet'
            })
          }
        }

        const filteredEvroamData = {
          type: 'FeatureCollection',
          features: []
        }
        const filteredChargenetData = {
          type: 'FeatureCollection',
          features: []
        }

        // Filter evroam dataset
        filteredEvroamData.features = evroamData.features.filter((feature) => {
          if (chargersOnRoute[feature.properties.siteId]) {
            if (feature.properties.installationStatus === 'Commissioned') {
              return true
            }
          }
        })
        context.commit('updateFilteredEvroamData', filteredEvroamData)

        // Filter Chargenet dataset
        filteredChargenetData.features = chargenetData.features.filter((feature) => {
          if (chargersOnRoute[feature.properties.name]) {
            if (feature.properties.status === 'Other Networks (CHAdeMO + CCS2)') {
              return true
            }
          }
        })
        context.commit('updateFilteredChargenetData', filteredChargenetData)

        const chargers = []
        // Add evroam chargers
        filteredEvroamData.features.forEach((charger) => {
          charger.properties.source = 'evroam'
          chargers.push(charger)
        })
        // Add chargenet chargers
        filteredChargenetData.features.forEach((charger) => {
          charger.properties.source = 'chargenet'
          chargers.push(charger)
        })

        const line = context.state.route
        for (const charger of chargers) {
          charger.properties.line = nearestPointOnLine(line, charger)
        }

        // Remove chargers within 10km of origin
        const remove = []
        for (const i in chargers) {
          if (chargers[i].properties.line.properties.location < 10) {
            remove.push(i)
          }
        }
        // Remove chargers within 10km of destination
        const maxDistance = line.properties.distance / 1000 - 10
        for (const i in chargers) {
          if (chargers[i].properties.line.properties.location > maxDistance) {
            remove.push(i)
          }
        }
        // Remove chargers
        remove.sort((a, b) => {
          return parseInt(a) - parseInt(b)
        })
        for (let i = 0; i < remove.length; i++) {
          chargers.splice(remove[i] - i, 1)
        }

        chargers.sort((a, b) => {
          const aPos = a.properties.line.properties.index
          const bPos = b.properties.line.properties.index
          return aPos - bPos
        })

        // Add origin placeholder
        const origin = {
          'type': 'Point',
          'coordinates': context.state.originCoords,
          'properties': {
            name: 'Origin',
            line: nearestPointOnLine(line, context.state.originCoords)
          }
        }
        chargers.unshift(origin)
        // Add destination placeholder
        const destination = {
          'type': 'Point',
          'coordinates': context.state.destinationCoords,
          properties: {
            name: 'Destination',
            line: nearestPointOnLine(line, context.state.destinationCoords)
          }
        }
        chargers.push(destination)

        context.commit('updateChargers', chargers)
      } else {
        console.log('Not ready to add chargers to route yet')
      }
    }
  },
  namespaced: true
}
